import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Plus, Search, FileText, LayoutGrid, List as ListIcon,
    CheckSquare, Trash2, IndianRupee, Ban, X, ChevronDown, Filter,
} from 'lucide-react';
import { api, fmtMoney, fmtDate } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { StatusBadge } from './InvoicingDashboard';

// Sprint 2026-07: bulk actions + advanced filters + Kanban toggle.
// The Kanban view groups issued invoices by status ⇄ payment stage so ops
// teams can see the funnel at a glance. Bulk actions (mark paid, cancel,
// delete drafts) go through /api/invoicing/invoices/bulk-action.

const KANBAN_COLUMNS = [
    { key: 'draft', label: 'Drafts', tone: 'bg-slate-100 text-slate-700' },
    { key: 'issued', label: 'Issued', tone: 'bg-blue-100 text-blue-700' },
    { key: 'partial', label: 'Partial', tone: 'bg-amber-100 text-amber-700' },
    { key: 'paid', label: 'Paid', tone: 'bg-emerald-100 text-emerald-700' },
    { key: 'cancelled', label: 'Cancelled', tone: 'bg-rose-100 text-rose-700' },
];

export default function InvoicesListPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [amountMin, setAmountMin] = useState('');
    const [amountMax, setAmountMax] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [view, setView] = useState(() =>
        // Persist view choice per browser so users don't re-toggle every visit.
        localStorage.getItem('emb.inv.view') || 'list',
    );
    const [selected, setSelected] = useState(new Set());
    const [pendingBulk, setPendingBulk] = useState(null); // {action}
    const [running, setRunning] = useState(false);
    const nav = useNavigate();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit: 200 };
            if (status && status !== 'all') params.status = status;
            if (q) params.q = q;
            const r = await api.invListInvoices(params);
            setItems(r.items || []);
            setSelected(new Set()); // clear selection on reload
        } catch (e) {
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { localStorage.setItem('emb.inv.view', view); }, [view]);

    // Client-side filters that don't need a backend round-trip.
    const filtered = useMemo(() => {
        const min = parseFloat(amountMin);
        const max = parseFloat(amountMax);
        return items.filter((inv) => {
            const g = Number(inv.grand_total || 0);
            if (Number.isFinite(min) && g < min) return false;
            if (Number.isFinite(max) && g > max) return false;
            if (dateFrom && (inv.invoice_date || '') < dateFrom) return false;
            if (dateTo && (inv.invoice_date || '') > dateTo) return false;
            return true;
        });
    }, [items, amountMin, amountMax, dateFrom, dateTo]);

    const activeFilterCount =
        (status !== 'all' ? 1 : 0) +
        (amountMin ? 1 : 0) +
        (amountMax ? 1 : 0) +
        (dateFrom ? 1 : 0) +
        (dateTo ? 1 : 0) +
        (q ? 1 : 0);

    const clearFilters = () => {
        setQ(''); setStatus('all');
        setAmountMin(''); setAmountMax('');
        setDateFrom(''); setDateTo('');
    };

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map((i) => i.id)));
    };

    const runBulk = async () => {
        if (!pendingBulk) return;
        setRunning(true);
        try {
            const res = await api.invBulkAction(pendingBulk.action, Array.from(selected));
            const okN = res.succeeded_count || 0;
            const failN = res.failed_count || 0;
            if (failN === 0) {
                toast.success(`${pendingBulk.label} · ${okN} invoice${okN === 1 ? '' : 's'} updated`);
            } else if (okN === 0) {
                toast.error(`${pendingBulk.label} failed for all ${failN} invoice${failN === 1 ? '' : 's'}`);
            } else {
                toast.warning(`${pendingBulk.label}: ${okN} succeeded, ${failN} skipped`);
            }
            setPendingBulk(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Bulk action failed');
        } finally {
            setRunning(false);
        }
    };

    const allSelected = filtered.length > 0 && selected.size === filtered.length;
    const someSelected = selected.size > 0 && !allSelected;

    return (
        <div className="space-y-4" data-testid="invoices-list-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-heading font-semibold tracking-tight">Invoices</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {filtered.length} invoice{filtered.length === 1 ? '' : 's'}
                        {items.length !== filtered.length && ` (of ${items.length})`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* View toggle */}
                    <div className="inline-flex rounded-md border border-input p-0.5 bg-background">
                        <Button
                            variant={view === 'list' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 px-2.5"
                            onClick={() => setView('list')}
                            data-testid="btn-view-list"
                        ><ListIcon size={14} className="mr-1" /> List</Button>
                        <Button
                            variant={view === 'kanban' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 px-2.5"
                            onClick={() => setView('kanban')}
                            data-testid="btn-view-kanban"
                        ><LayoutGrid size={14} className="mr-1" /> Kanban</Button>
                    </div>
                    <Button asChild data-testid="btn-new-invoice">
                        <Link to="/invoicing/invoices/new"><Plus size={16} className="mr-1.5" /> New invoice</Link>
                    </Button>
                </div>
            </div>

            {/* Filter bar */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                data-testid="invoices-search"
                                placeholder="Invoice number or customer..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                                className="pl-9"
                            />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="filter-status" className="w-[150px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="issued">Issued</SelectItem>
                                <SelectItem value="partial">Partial</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="btn-advanced-filters">
                                    <Filter size={14} className="mr-1" /> Advanced
                                    {activeFilterCount > 0 && (
                                        <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">
                                            {activeFilterCount}
                                        </Badge>
                                    )}
                                    <ChevronDown size={12} className="ml-1" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-80" data-testid="advanced-filter-popover">
                                <div className="space-y-3">
                                    <div className="text-xs font-medium">Amount range (₹)</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input type="number" placeholder="Min" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} data-testid="filter-amount-min" />
                                        <Input type="number" placeholder="Max" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} data-testid="filter-amount-max" />
                                    </div>
                                    <div className="text-xs font-medium">Invoice date range</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
                                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-border">
                                        <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="filter-clear">
                                            <X size={12} className="mr-1" /> Clear all
                                        </Button>
                                        <Button size="sm" onClick={load} data-testid="filter-apply">Apply</Button>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                        <Button variant="outline" size="sm" onClick={load} data-testid="btn-search">Search</Button>
                    </div>
                </CardHeader>

                {/* Bulk action bar — appears only when at least one row is selected. */}
                {selected.size > 0 && (
                    <div className="flex items-center justify-between px-4 pb-3 -mt-2" data-testid="bulk-bar">
                        <div className="text-sm">
                            <span className="font-medium">{selected.size}</span> selected
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => setPendingBulk({ action: 'mark_paid', label: 'Mark paid' })} data-testid="bulk-mark-paid">
                                <IndianRupee size={14} className="mr-1" /> Mark paid
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setPendingBulk({ action: 'cancel', label: 'Cancel' })} data-testid="bulk-cancel">
                                <Ban size={14} className="mr-1" /> Cancel
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => setPendingBulk({ action: 'delete', label: 'Delete drafts' })} data-testid="bulk-delete">
                                <Trash2 size={14} className="mr-1" /> Delete drafts
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
                                <X size={14} />
                            </Button>
                        </div>
                    </div>
                )}

                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6"><Skeleton className="h-40 w-full" /></div>
                    ) : filtered.length === 0 ? (
                        <EmptyState />
                    ) : view === 'list' ? (
                        <InvoiceTable
                            items={filtered}
                            selected={selected}
                            toggleSelect={toggleSelect}
                            toggleSelectAll={toggleSelectAll}
                            allSelected={allSelected}
                            someSelected={someSelected}
                            nav={nav}
                        />
                    ) : (
                        <InvoiceKanban items={filtered} nav={nav} />
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!pendingBulk} onOpenChange={(v) => !v && setPendingBulk(null)}>
                <AlertDialogContent data-testid="bulk-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm bulk action</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground">
                                Apply <strong>{pendingBulk?.label}</strong> to
                                {' '}<strong>{selected.size}</strong> invoice{selected.size === 1 ? '' : 's'}?
                                <ul className="mt-2 list-disc pl-5 text-xs">
                                    <li><strong>Mark paid</strong> creates a settling payment for the full outstanding balance.</li>
                                    <li><strong>Cancel</strong> preserves the invoice number (GST requirement).</li>
                                    <li><strong>Delete drafts</strong> only removes invoices in draft status — issued ones are skipped.</li>
                                </ul>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={running}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={runBulk} disabled={running} data-testid="bulk-confirm">
                            {running ? 'Working…' : `${pendingBulk?.label || 'Apply'}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="text-center py-16 px-4" data-testid="empty-state">
            <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <FileText size={26} className="text-primary" />
            </div>
            <div className="font-medium mb-1">No invoices match those filters</div>
            <div className="text-sm text-muted-foreground mb-4">
                Try widening the filters, or create your first invoice to get started.
            </div>
            <Button asChild data-testid="empty-create">
                <Link to="/invoicing/invoices/new"><Plus size={14} className="mr-1" /> Create your first invoice</Link>
            </Button>
        </div>
    );
}

function InvoiceTable({ items, selected, toggleSelect, toggleSelectAll, allSelected, someSelected, nav }) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-10">
                        <Checkbox
                            checked={allSelected || (someSelected ? 'indeterminate' : false)}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all"
                            data-testid="select-all"
                        />
                    </TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((inv) => {
                    const isSel = selected.has(inv.id);
                    return (
                        <TableRow
                            key={inv.id}
                            data-testid={`invoice-row-${inv.id}`}
                            className={`cursor-pointer ${isSel ? 'bg-accent/30' : ''}`}
                        >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={isSel}
                                    onCheckedChange={() => toggleSelect(inv.id)}
                                    aria-label={`Select ${inv.invoice_number || 'draft'}`}
                                    data-testid={`select-${inv.id}`}
                                />
                            </TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="font-mono text-xs">
                                {inv.invoice_number || <span className="text-muted-foreground italic">DRAFT</span>}
                            </TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="font-medium">{inv.customer_snapshot?.name || '—'}</TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="text-xs text-muted-foreground">{fmtDate(inv.invoice_date)}</TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="text-xs text-muted-foreground">{fmtDate(inv.due_date)}</TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="text-right tabular-nums">{fmtMoney(inv.grand_total)}</TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="text-right tabular-nums text-xs">{fmtMoney(inv.amount_due)}</TableCell>
                            <TableCell onClick={() => nav(`/invoicing/invoices/${inv.id}`)} className="text-right"><StatusBadge status={inv.status} overdue={inv.overdue} /></TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function InvoiceKanban({ items, nav }) {
    const buckets = KANBAN_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: [] }), {});
    items.forEach((inv) => {
        const key = inv.status || 'draft';
        if (buckets[key]) buckets[key].push(inv);
    });
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 p-4" data-testid="kanban-board">
            {KANBAN_COLUMNS.map((col) => (
                <div key={col.key} className="flex flex-col gap-2" data-testid={`kanban-col-${col.key}`}>
                    <div className={`flex items-center justify-between px-3 py-2 rounded-md ${col.tone}`}>
                        <span className="text-xs font-semibold uppercase tracking-wide">{col.label}</span>
                        <span className="text-xs font-medium tabular-nums">{buckets[col.key].length}</span>
                    </div>
                    <div className="flex flex-col gap-2 min-h-[60px]">
                        {buckets[col.key].length === 0 && (
                            <div className="text-[11px] text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                                Nothing here
                            </div>
                        )}
                        {buckets[col.key].map((inv) => (
                            <div
                                key={inv.id}
                                onClick={() => nav(`/invoicing/invoices/${inv.id}`)}
                                data-testid={`kanban-card-${inv.id}`}
                                className="rounded-md border border-border bg-background p-3 cursor-pointer hover:shadow-sm hover:border-primary/40 transition-all"
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="font-mono text-[11px] text-muted-foreground truncate">
                                        {inv.invoice_number || 'DRAFT'}
                                    </span>
                                    {inv.overdue && <Badge variant="destructive" className="h-4 px-1 text-[9px]">OVERDUE</Badge>}
                                </div>
                                <div className="text-sm font-medium truncate">{inv.customer_snapshot?.name || '—'}</div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-muted-foreground">{fmtDate(inv.invoice_date)}</span>
                                    <span className="text-sm font-semibold tabular-nums">{fmtMoney(inv.grand_total)}</span>
                                </div>
                                {(inv.amount_due || 0) > 0 && (inv.status === 'partial' || inv.status === 'issued') && (
                                    <div className="text-[11px] text-amber-700 mt-1 tabular-nums">Due {fmtMoney(inv.amount_due)}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
